import type { MemberTableRow } from './types';
import type { MembersTableSortBy } from '@/store/api/userApi';
import type { ReactNode } from 'react';

export type MembersTableColumnKey =
  | MembersTableSortBy
  | 'cohort_tags'
  | 'date_paused'
  | 'return_date'
  | 'growth_plan_started'
  | 'insights_status'
  | 'sessions';

export interface MembersTableColumn {
  key: MembersTableColumnKey;
  label: string;
  renderCell?: (row: MemberTableRow) => ReactNode;
  sortable: boolean;
}

export type MembersTableFilterKey =
  | 'company'
  | 'cohort'
  | 'memberStatus'
  | 'businessPlan'
  | 'coach'
  | 'endDateStatus'
  | 'coachesAssigned'
  | 'growthPlanStarted'
  | 'insightsStatus'
  | 'seatType'
  | 'sessions';

export const ALL_MEMBERS_TABLE_COLUMNS: MembersTableColumn[] = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'company', label: 'Company', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'member_status', label: 'Status', sortable: true },
  { key: 'seat_type', label: 'Seat Type', sortable: true },
  { key: 'joined_at', label: 'Joined', sortable: true },
  { key: 'cohort_tags', label: 'Cohort', sortable: false },
  { key: 'contract_end_date', label: 'Contract End Date', sortable: true },
  { key: 'end_date_status', label: 'End Date Status', sortable: true },
  { key: 'coach_name', label: 'Coach', sortable: true },
  { key: 'sessions', label: 'Sessions', sortable: false },
];

// Paused comes from HubSpot and is an Ops-only view; the backend sends pause dates only to admins and support.
export const ALL_MEMBERS_TABLE_COLUMNS_WITH_PAUSE: MembersTableColumn[] =
  ALL_MEMBERS_TABLE_COLUMNS.flatMap((column) =>
    column.key === 'member_status'
      ? [
          column,
          { key: 'date_paused', label: 'Date Paused', sortable: false },
          { key: 'return_date', label: 'Return Date', sortable: false },
        ]
      : [column],
  );

export const ALL_MEMBERS_TABLE_FILTERS: MembersTableFilterKey[] = [
  'company',
  'memberStatus',
  'businessPlan',
  'coach',
  'endDateStatus',
  'coachesAssigned',
  'growthPlanStarted',
  'insightsStatus',
  'seatType',
  'sessions',
];

export const COMPANY_ADMIN_MEMBERS_TABLE_COLUMNS: MembersTableColumn[] = [
  { key: 'name', label: 'Name', sortable: true },
  { key: 'member_status', label: 'Status', sortable: true },
  { key: 'seat_type', label: 'Seat Type', sortable: true },
  { key: 'joined_at', label: 'Joined', sortable: true },
  { key: 'insights_status', label: 'Insights Status', sortable: true },
  { key: 'contract_end_date', label: 'Contract End Date', sortable: true },
  { key: 'cohort_tags', label: 'Cohort', sortable: false },
  { key: 'end_date_status', label: 'End Date Status', sortable: true },
  { key: 'admin_note', label: 'Admin Notes', sortable: true },
  { key: 'sessions', label: 'Sessions', sortable: false },
];

export const COMPANY_ADMIN_MEMBERS_TABLE_FILTERS: MembersTableFilterKey[] = [
  'cohort',
  'memberStatus',
  'endDateStatus',
  'insightsStatus',
  'seatType',
  'sessions',
];
